import clsx from "clsx";
import { forwardRef } from "react";

import { CrossIcon } from "../Icons";

type SearchInputV2Props = {
  id?: string;
  name?: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  "aria-label"?: string;
  "data-testid"?: string;
  error?: boolean;
  loading?: boolean;
  /**
   * Accessible label for the clear button. The button only renders when there is
   * a value and `loading` is false — the spinner takes its place mid-search.
   * Pass a translated string; the fallback is English.
   */
  clearLabel?: string;
  /**
   * Whether a populated field offers a clear button. Defaults to `true` (toolbar
   * search). Set `false` where the design has no clear affordance — the
   * create-program email gate (canvas `.wiz-search`) is icon + input only, and
   * its value is cleared by the flow itself rather than by the user.
   */
  clearable?: boolean;
  className?: string;
  /**
   * Focuses the field on mount. Used by the create-flow email lookup, where the
   * modal opens for the sole purpose of typing an email — the canvas's gate input
   * carries `autoFocus` for the same reason.
   */
  autoFocus?: boolean;
};

/** DS search field — tonal-grey fill, transparent border at rest, brand-blue
 * ring on focus, red on error. Used for toolbar search and the create-flow
 * email lookup. */
export const SearchInputV2 = forwardRef<HTMLInputElement, SearchInputV2Props>(
  (
    {
      id,
      name,
      type = "search",
      value,
      onChange,
      onKeyDown,
      placeholder,
      "aria-label": ariaLabel,
      "data-testid": testId,
      error = false,
      loading = false,
      clearLabel = "Clear search",
      clearable = true,
      className,
      autoFocus = false,
    },
    ref,
  ) => {
    return (
      <div
        className={clsx(
          "relative flex h-10 w-full items-center rounded-lg border-2 bg-surface-tonal-default pl-10 pr-3.5 transition-colors",
          error
            ? "border-text-negative-default focus-within:border-text-negative-default"
            : "border-transparent focus-within:border-surface-brand-default",
          className,
        )}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
          className="pointer-events-none absolute left-3.5 text-icon-tertiary"
        >
          <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M11 11l3 3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
        <input
          ref={ref}
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label={ariaLabel}
          data-testid={testId}
          aria-invalid={error}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          className="typo-body-sm h-full flex-1 bg-transparent text-text-primary outline-none placeholder:text-text-subdued [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
        />
        {loading ? (
          <span
            className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-surface-tonal-bold border-t-surface-brand-default"
            aria-hidden="true"
          />
        ) : (
          clearable &&
          value.length > 0 && (
            // DS `.search__clear`: 20px hit area, 4px radius, hover tint, with a
            // 14px `--icon-secondary` cross inside.
            <button
              type="button"
              onClick={() => onChange("")}
              aria-label={clearLabel}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors hover:bg-overlay-opacity-state-hover"
            >
              <CrossIcon
                className="h-3.5 w-3.5 text-icon-secondary"
                aria-hidden="true"
                focusable="false"
              />
            </button>
          )
        )}
      </div>
    );
  },
);

SearchInputV2.displayName = "SearchInputV2";
